import {
  AuditFilter,
  ShopAudit,
  countStatuses,
  filterLists,
  filterSettings,
  summarizeShop,
} from "@/lib/shopConfigAuditView";
import { ListChips } from "./ListChips";
import { SettingsGrid } from "./SettingsGrid";
import { ShopConfigAuditPanelHeader } from "./ShopConfigAuditPanelHeader";
import { ShopConfigAuditSection } from "./ShopConfigAuditSection";

interface ShopConfigAuditPanelProps {
  shopId: string;
  shopName: string;
  audit: ShopAudit;
  // Controlled by the page, not held here: the panel unmounts whenever the
  // loaded page's rows are momentarily empty (paging), which would otherwise
  // reset the tab to Issues. Section collapse state is deliberately local and
  // does reset per shop (the sections container below is keyed by shopId).
  filter: AuditFilter;
  onFilterChange: (filter: AuditFilter) => void;
  onClose: () => void;
}

export const ShopConfigAuditPanel = ({
  shopId,
  shopName,
  audit,
  filter,
  onFilterChange,
  onClose,
}: ShopConfigAuditPanelProps) => {
  const summary = summarizeShop(audit);

  const settingSections = [
    { key: "shopConfig", title: "Config Defaults", result: audit.shopConfig },
    { key: "cardColors", title: "Card Colors", result: audit.cardColors },
    {
      key: "enabledFeatures",
      title: "Feature Flags",
      result: audit.enabledFeatures,
    },
  ].flatMap(({ key, title, result }) => {
    const visible = filterSettings(result.rows, filter);
    if (visible.length === 0) return [];
    const { missing, drift } = countStatuses(result.rows);
    return [
      <ShopConfigAuditSection
        key={key}
        title={title}
        missing={missing}
        drift={drift}
      >
        <SettingsGrid rows={visible} />
      </ShopConfigAuditSection>,
    ];
  });

  const listSections = [
    { key: "tags", title: "Tags", list: audit.tags },
    { key: "automations", title: "Automations", list: audit.automations },
  ].flatMap(({ key, title, list }) => {
    if (audit.fetchError) {
      return [
        <ShopConfigAuditSection
          key={key}
          title={title}
          missing={0}
          drift={0}
          error
        >
          <p className="px-4 py-3 text-sm text-destructive">
            Failed to load: {audit.fetchError}
          </p>
        </ShopConfigAuditSection>,
      ];
    }
    const visible = filterLists(list, filter);
    if (
      visible.missing.length === 0 &&
      visible.extra.length === 0 &&
      visible.present.length === 0
    ) {
      return [];
    }
    return [
      <ShopConfigAuditSection
        key={key}
        title={title}
        missing={list.missing.length}
        drift={0}
      >
        <ListChips lists={visible} />
      </ShopConfigAuditSection>,
    ];
  });

  const sections = [...settingSections, ...listSections];

  return (
    <aside
      aria-label={`${shopName} config audit`}
      className="flex w-full flex-col overflow-y-auto bg-background text-foreground shadow-[-8px_0_24px_rgba(0,0,0,0.35)] lg:h-full lg:w-[520px] lg:shrink-0"
    >
      <ShopConfigAuditPanelHeader
        shopName={shopName}
        summary={summary}
        listsUnavailable={Boolean(audit.fetchError)}
        filter={filter}
        onFilterChange={onFilterChange}
        onClose={onClose}
      />
      <div key={shopId} className="flex flex-col gap-3 px-6 pb-8 pt-4">
        {sections.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            Nothing in this view.
          </div>
        ) : (
          sections
        )}
      </div>
    </aside>
  );
};
